/**
 * Privacy and evidence rules shared by the browser reporter and the server
 * intake.  This module deliberately has no browser or server dependencies so
 * that the ErrorBoundary can use the same rules without making a network probe.
 */

export const CLIENT_ERROR_SOURCES = [
  "client-error-boundary",
  "window-onerror",
  "unhandledrejection",
  "hydration-error",
  "resource-load-error",
  // Kept for clients deployed before resource-load-error was introduced.
  "img-404",
] as const;

export type ClientErrorSource = (typeof CLIENT_ERROR_SOURCES)[number];

export const RESOURCE_TYPES = ["element", "image", "script", "stylesheet"] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];

export const RESOURCE_CLASSIFICATIONS = [
  "offline",
  "missing",
  "http-error",
  "mime-error",
  "network-or-unknown",
] as const;
export type ResourceClassification = (typeof RESOURCE_CLASSIFICATIONS)[number];

/** Coarse, privacy-safe attribution for a client failure. */
export const CLIENT_ERROR_ATTRIBUTIONS = ["app", "library", "external-bridge", "unknown"] as const;
export type ClientErrorAttribution = (typeof CLIENT_ERROR_ATTRIBUTIONS)[number];

export const BROWSER_FAMILIES = [
  "chrome",
  "edge",
  "firefox",
  "safari",
  "opera",
  "samsung",
  "android-webview",
  "other",
] as const;
export type BrowserFamily = (typeof BROWSER_FAMILIES)[number];

export const RECOVERY_KINDS = ["chunk", "css-preload", "offline", "other"] as const;
export type RecoveryKind = (typeof RECOVERY_KINDS)[number];

const SOURCE_SET = new Set<string>(CLIENT_ERROR_SOURCES);
const RESOURCE_TYPE_SET = new Set<string>(RESOURCE_TYPES);
const RESOURCE_CLASSIFICATION_SET = new Set<string>(RESOURCE_CLASSIFICATIONS);
const ATTRIBUTION_SET = new Set<string>(CLIENT_ERROR_ATTRIBUTIONS);
const BROWSER_FAMILY_SET = new Set<string>(BROWSER_FAMILIES);
const RECOVERY_KIND_SET = new Set<string>(RECOVERY_KINDS);

function boundedString(value: unknown, max: number): string {
  return typeof value === "string" ? value.slice(0, max) : "";
}

function redactEmail(value: string): string {
  return value.replace(
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
    "[redacted-email]",
  );
}

function redactSensitivePath(pathname: string): string {
  let redactNextSegment = false;
  return pathname
    .split("/")
    .map((segment) => {
      if (!segment) return segment;
      const decoded = (() => {
        try {
          return decodeURIComponent(segment);
        } catch {
          return segment;
        }
      })();
      const extension = decoded.match(/^(.+?)(\.[A-Za-z0-9]{1,10})$/);
      const tokenCandidate = extension ? extension[1] : decoded;
      const isSensitiveRoute = /(?:^|[-_])(upload|token|secret|key|auth|session|password)(?:[-_]|$)/i.test(decoded);
      // Numeric IDs, UUIDs, opaque token-looking segments and named secrets
      // are not useful for aggregate telemetry and can contain personal data.
      if (
        redactNextSegment ||
        /^\d+$/.test(tokenCandidate) ||
        /^[a-f0-9]{16,}$/i.test(tokenCandidate) ||
        /^[a-f0-9]{8}-[a-f0-9-]{13,}$/i.test(tokenCandidate) ||
        /(?:^|[-_])(token|secret|key|auth|session|password)(?:[-_]|$)/i.test(tokenCandidate) ||
        /^[A-Za-z0-9_-]{28,}$/.test(tokenCandidate)
      ) {
        redactNextSegment = false;
        return `[redacted]${extension ? extension[2] : ""}`;
      }
      // Upload/password/token routes are commonly followed by short opaque
      // values (for example /upload-foto/ab12cd). Do not require a 28-char
      // token heuristic in this known-sensitive route context.
      redactNextSegment = isSensitiveRoute;
      return redactEmail(decoded).slice(0, 160);
    })
    .join("/");
}

/**
 * Returns an origin and path only. Query strings/fragments are never sent,
 * including for same-origin URLs, because they commonly carry tokens and
 * customer identifiers.
 */
export function sanitizeTelemetryUrl(value: unknown, max = 500): string {
  if (typeof value !== "string" || !value) return "";
  const raw = value.trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw, "http://telemetry.invalid");
    const pathname = redactSensitivePath(parsed.pathname || "/");
    const isRelative = !/^[a-z][a-z\d+\-.]*:/i.test(raw) && !raw.startsWith("//");
    const result = isRelative
      ? pathname
      : `${parsed.origin === "http://telemetry.invalid" ? "" : parsed.origin}${pathname}`;
    return redactEmail(result).slice(0, max);
  } catch {
    // Keep useful path-like context from malformed/old client payloads while
    // still dropping query and fragment material.
    const pathOnly = raw.split(/[?#]/, 1)[0];
    return redactEmail(redactSensitivePath(pathOnly)).slice(0, max);
  }
}

/**
 * Redacts stack/message text without attempting to parse or execute it.
 * URLs are passed through the URL sanitizer, so query/fragment secrets are
 * removed from stacks as well as from the explicit URL field.
 */
export function sanitizeTelemetryText(value: unknown, max = 4000): string {
  if (typeof value !== "string" || !value) return "";
  let text = redactEmail(value);
  text = text.replace(
    /\b(?:https?|wss?):\/\/[^\s"'<>]+/gi,
    (url) => sanitizeTelemetryUrl(url, 500),
  );
  // Error strings and stacks often contain relative asset/upload URLs. They
  // have the same query/fragment exposure as absolute URLs, but do not have a
  // scheme for the rule above to match.
  text = text.replace(
    /(^|[\s("'=])((?:\/[A-Za-z0-9._~!$&'()*+,;=:@%+\-/]+)+[?#][^\s"'<>]*)/g,
    (_match, prefix: string, url: string) => `${prefix}${sanitizeTelemetryUrl(url, 500)}`,
  );
  // Redact the complete bearer credential, including the common
  // `Authorization: Bearer   value` spelling. Keep only a non-sensitive
  // marker after the label.
  text = text.replace(
    /\b(authorization\s*:\s*)bearer\s+[^\s"'`,;)\]]+/gi,
    "$1[redacted]",
  );
  text = text.replace(
    /\b(bearer\s+)[^\s"'`,;)\]]+/gi,
    "$1[redacted]",
  );
  text = text.replace(
    /\b((?:token|secret|api[_-]?key|authorization)\s*[=:]\s*)(?:"[^"]*"|'[^']*'|[^\s"',;]+)/gi,
    "$1[redacted]",
  );
  text = text.replace(
    /\b[a-f0-9]{8}-[a-f0-9-]{13,}\b/gi,
    "[redacted]",
  );
  return text.slice(0, max);
}

export function normalizeResponseStatus(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  return value >= 100 && value <= 599 ? value : null;
}

/**
 * Classifies only facts the browser can know. In particular, a resource
 * error event alone is not evidence of HTTP 404: it is network-or-unknown.
 */
export function classifyResourceLoadError(input: {
  online?: boolean | null;
  responseStatus?: unknown;
  mimeMismatch?: boolean | null;
}): ResourceClassification {
  if (input.online === false) return "offline";
  const status = normalizeResponseStatus(input.responseStatus);
  if (status === 404 || status === 410) return "missing";
  if (status !== null && status >= 400) return "http-error";
  if (input.mimeMismatch === true) return "mime-error";
  return "network-or-unknown";
}

export function isClientErrorSource(value: unknown): value is ClientErrorSource {
  return typeof value === "string" && SOURCE_SET.has(value);
}

export function isResourceType(value: unknown): value is ResourceType {
  return typeof value === "string" && RESOURCE_TYPE_SET.has(value);
}

export function isResourceClassification(value: unknown): value is ResourceClassification {
  return typeof value === "string" && RESOURCE_CLASSIFICATION_SET.has(value);
}

export function isClientErrorAttribution(value: unknown): value is ClientErrorAttribution {
  return typeof value === "string" && ATTRIBUTION_SET.has(value);
}

export function isBrowserFamily(value: unknown): value is BrowserFamily {
  return typeof value === "string" && BROWSER_FAMILY_SET.has(value);
}

export function isRecoveryKind(value: unknown): value is RecoveryKind {
  return typeof value === "string" && RECOVERY_KIND_SET.has(value);
}

/**
 * Infer ownership only from stack frames. Error messages are intentionally not
 * considered: words such as "bridge", "react", or a package name in an
 * exception string are not evidence of where the failure ran. Application
 * frames win over dependency frames so a library mentioned by an app stack
 * cannot misattribute the incident.
 */
export function classifyClientErrorAttribution(input: {
  stack?: unknown;
  componentStack?: unknown;
}): ClientErrorAttribution {
  const stack = typeof input.stack === "string" ? input.stack : "";
  const componentStack = typeof input.componentStack === "string" ? input.componentStack : "";
  const stackFrames = stack
    .split(/\r?\n/)
    .filter((line) => /^\s*at\b/.test(line))
    .join("\n");

  // React's component stack is app evidence when it contains a named
  // component. A bare/unknown component stack is not enough.
  const hasAppFrame =
    /(?:^|[\s(])(?:https?:\/\/[^)\s]+)?(?:\/|\\)(?:client\/)?src\/[^\s):]+/i.test(stack) ||
    /\b(?:src|client\/src)\/[A-Za-z0-9_./-]+\.[cm]?[jt]sx?(?::\d+(?::\d+)?)?/i.test(stack) ||
    /^\s*at\s+[A-Z][A-Za-z0-9_$]*(?:\s|\(|$)/m.test(componentStack);
  if (hasAppFrame) return "app";

  // Browser extension/native bridge URLs are strong evidence. A filename or
  // the word "bridge" alone is deliberately insufficient.
  if (
    /(?:^|[\s(])(?:chrome|moz|safari)-extension:\/\/|(?:^|[\s(])extension:\/\//i.test(stackFrames) ||
    /(?:^|[\s(])(?:capacitor|ionic):\/\//i.test(stackFrames)
  ) {
    return "external-bridge";
  }

  // Dependency paths and well-known runtime package frames are useful only
  // when no application frame was present.
  if (
    /(?:^|[/\\])node_modules(?:[/\\])/i.test(stackFrames) ||
    /\b(?:react|react-dom|scheduler|wouter|vite|webpack|rollup|esbuild)\b/i.test(stackFrames)
  ) {
    return "library";
  }
  return "unknown";
}

function validBuildIdentity(value: unknown): string {
  if (typeof value !== "string") return "";
  const candidate = value.trim();
  // Only a Vite-style content hash (base64url-like filename token) is useful
  // and safe to persist. Never
  // accept a script URL, path, query string, or arbitrary release label.
  return /^[A-Za-z0-9_-]{6,64}$/.test(candidate) ? candidate : "";
}

function validBrowserMajor(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 999
    ? value
    : null;
}

export interface SanitizedClientErrorPayload {
  source: ClientErrorSource;
  message: string;
  stack: string;
  url: string;
  componentStack: string;
  route: string;
  resourceUrl: string;
  resourceType: ResourceType | null;
  classification: ResourceClassification | null;
  responseStatus: number | null;
  online: boolean | null;
  attribution: ClientErrorAttribution;
  clientBuildId: string;
  browserFamily: BrowserFamily | null;
  browserMajor: number | null;
  supportsArrayAt: boolean | null;
  supportsStringAt: boolean | null;
  recoveryKind: RecoveryKind | null;
  isChunkError: boolean;
  reloadSuppressed: boolean;
  exceededThreshold: boolean;
  resourceFailuresThisSession: number | null;
  sampleUrls: string[];
}

/**
 * Server-side boundary for old and untrusted clients. Unknown fields are
 * intentionally ignored; callers should only persist this allowlisted shape.
 */
export function sanitizeClientErrorPayload(value: unknown): SanitizedClientErrorPayload {
  const input = value && typeof value === "object"
    ? value as Record<string, unknown>
    : {};
  const source = isClientErrorSource(input.source) ? input.source : "client-error-boundary";
  const legacyResource = source === "img-404";
  const responseStatus = legacyResource ? null : normalizeResponseStatus(input.responseStatus);
  const online = typeof input.online === "boolean" ? input.online : null;
  const isResourceEvidenceReport =
    source === "resource-load-error" ||
    (input.isChunkError === true &&
      (isResourceType(input.resourceType) || online === false || responseStatus !== null));
  const classification = isResourceEvidenceReport
    ? classifyResourceLoadError({
        online,
        responseStatus,
        mimeMismatch: input.mimeMismatch === true,
      })
    : null;

  return {
    source,
    message: sanitizeTelemetryText(input.message, 1000),
    stack: sanitizeTelemetryText(input.stack, 4000),
    url: sanitizeTelemetryUrl(input.url, 500),
    componentStack: sanitizeTelemetryText(input.componentStack, 4000),
    route: sanitizeTelemetryUrl(input.route, 300),
    resourceUrl: sanitizeTelemetryUrl(input.resourceUrl ?? input.imageUrl, 500),
    resourceType: isResourceType(input.resourceType)
      ? input.resourceType
      : source === "img-404"
        ? "image"
        : null,
    // A legacy img-404 report deliberately has no HTTP classification.
    classification: legacyResource ? "network-or-unknown" : classification,
    responseStatus,
    online,
    attribution: classifyClientErrorAttribution({
      stack: input.stack,
      componentStack: input.componentStack,
    }),
    clientBuildId: validBuildIdentity(input.clientBuildId),
    browserFamily: isBrowserFamily(input.browserFamily) ? input.browserFamily : null,
    browserMajor: validBrowserMajor(input.browserMajor),
    supportsArrayAt: typeof input.supportsArrayAt === "boolean" ? input.supportsArrayAt : null,
    supportsStringAt: typeof input.supportsStringAt === "boolean" ? input.supportsStringAt : null,
    recoveryKind: isRecoveryKind(input.recoveryKind) ? input.recoveryKind : null,
    isChunkError: input.isChunkError === true,
    reloadSuppressed: input.reloadSuppressed === true,
    exceededThreshold: input.exceededThreshold === true,
    resourceFailuresThisSession:
      typeof input.resourceFailuresThisSession === "number" &&
      Number.isFinite(input.resourceFailuresThisSession)
        ? Math.max(0, Math.min(1000, Math.floor(input.resourceFailuresThisSession)))
        : null,
    sampleUrls: Array.isArray(input.sampleUrls)
      ? input.sampleUrls
          .slice(0, 5)
          .map((url) => sanitizeTelemetryUrl(url))
          .filter(Boolean)
      : [],
  };
}